// eslint-disable-next-line eag/match-named-export
import {z} from 'zod';

import {i18n} from '@dms/i18n';

export const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

const CLOSED_TIME = '00:00';

function isClosedSlot(slot: {from?: string | null; to?: string | null}) {
  const from = slot.from?.trim().substring(0, 5) ?? '';
  const to = slot.to?.trim().substring(0, 5) ?? '';
  return from === CLOSED_TIME && to === CLOSED_TIME;
}

const TimeSlotSchema = z
  .object({
    from: z.string().optional().nullable(),
    to: z.string().optional().nullable(),
  })
  .superRefine((slot, ctx) => {
    const fromSet = Boolean(slot.from?.trim());
    const toSet = Boolean(slot.to?.trim());
    if (fromSet && !toSet) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('entity.branch.validations.fillBothTimesOrLeaveBothEmpty'),
        path: ['to'],
      });
      return;
    }
    if (!fromSet && toSet) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('entity.branch.validations.fillBothTimesOrLeaveBothEmpty'),
        path: ['from'],
      });
      return;
    }
    if (fromSet && toSet && !isClosedSlot(slot) && slot.from! >= slot.to!) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('entity.branch.validations.fromTimeMustBeBeforeToTime'),
        path: ['to'],
      });
    }
  });

const DaySlotsSchema = z.object({
  monday: z.array(TimeSlotSchema).optional(),
  tuesday: z.array(TimeSlotSchema).optional(),
  wednesday: z.array(TimeSlotSchema).optional(),
  thursday: z.array(TimeSlotSchema).optional(),
  friday: z.array(TimeSlotSchema).optional(),
  saturday: z.array(TimeSlotSchema).optional(),
  sunday: z.array(TimeSlotSchema).optional(),
});

export type DaySlots = z.infer<typeof DaySlotsSchema>;

export type OpeningHoursCategory =
  | 'salesOpeningHours'
  | 'workshopOpeningHours'
  | 'rentalOpeningHours';

const toHHMM = (time: string) => time.substring(0, 5);

const toHHMMSS = (time: string) => (time.length === 5 ? `${time}:00` : time);

const CLOSED_SLOT = {from: CLOSED_TIME, to: CLOSED_TIME};

function buildDefaultDaySlots(override?: Partial<DaySlots>): DaySlots {
  const overrideMap = override ?? {};
  return Object.fromEntries(
    DAYS.map((day) => {
      const slots = overrideMap[day];
      const hasSlots = Array.isArray(slots) && slots.length > 0;
      if (!hasSlots) {
        return [day, [CLOSED_SLOT]];
      }
      return [
        day,
        slots?.map((s) => ({
          from: toHHMM(s.from ?? '') || CLOSED_TIME,
          to: toHHMM(s.to ?? '') || CLOSED_TIME,
        })),
      ];
    })
  ) as DaySlots;
}

type TimeSlotInput = {from?: string | null; to?: string | null};

function isSlotComplete(slot: TimeSlotInput): slot is {from: string; to: string} {
  const from = slot.from?.trim();
  const to = slot.to?.trim();
  return Boolean(from && to);
}

function isOpenSlot(slot: TimeSlotInput): slot is {from: string; to: string} {
  return isSlotComplete(slot) && !isClosedSlot(slot);
}

export function normalizeDaySlotsToHHMMSS(daySlots: DaySlots): DaySlots {
  return Object.fromEntries(
    DAYS.filter((day) => daySlots[day] !== undefined).map((day) => {
      const slots = daySlots[day];
      const openSlots = slots?.filter(isOpenSlot);
      return [
        day,
        openSlots?.map((s) => ({
          from: toHHMMSS(s.from),
          to: toHHMMSS(s.to),
        })),
      ];
    })
  ) as DaySlots;
}

export const CategoryOpeningHoursFormSchema = z.object({
  branchIds: z.array(z.string().uuid()),
  openingHours: DaySlotsSchema.nullable(),
});

export type CategoryOpeningHoursFormValues = z.infer<typeof CategoryOpeningHoursFormSchema>;

export function buildCategoryDefaultValues(
  branchId: string | undefined,
  openingHours: Partial<DaySlots> | null | undefined
) {
  return {
    branchIds: branchId ? [branchId] : [],
    openingHours: buildDefaultDaySlots(openingHours ?? undefined),
  };
}
